<script lang="ts">
    import { onDestroy, onMount } from 'svelte';
    import { auth } from '../../framework/js/auth.js';
    import { api } from '../../framework/js/api.js';
    import { toast } from '../../framework/js/toast.js';
    import ModalConfirm from '../../framework/components/ModalConfirm.svelte';
    import {
        AppEmptyState,
        AppLoadingState,
        AppPageHeader,
        AppToolbar,
        StatusBadge,
    } from '../../framework/components/app';
    import { isKnowledgeIndexing, type KnowledgeSourceItem } from '../admin/knowledge/knowledgeAdmin';
    import TeamKnowledgeFormModal from './TeamKnowledgeFormModal.svelte';
    import {
        formatTeamFileSize,
        formatTeamKnowledgeDate,
        teamKnowledgeStatusLabel,
        teamKnowledgeStatusTone,
        teamKnowledgeFailedHint,
        teamTileIcon,
        teamTypeLabel,
    } from './teamKnowledge';

    const POLL_INTERVAL_MS = 2500;
    const MAX_POLL_MS = 5 * 60 * 1000;

    let items: KnowledgeSourceItem[] = [];
    let filtered: KnowledgeSourceItem[] = [];
    let isLoading = true;
    let canManage = false;
    let search = '';
    let modalOpen = false;
    let saving = false;
    let editing: KnowledgeSourceItem | null = null;
    let deleteTarget: KnowledgeSourceItem | null = null;
    let showDeleteConfirm = false;
    let retryingUuid: string | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let pollStartedAt = 0;
    let isPolling = false;

    $: teamUuid = $auth?.user?.active_team_uuid;
    $: userUuid = $auth?.user?.uuid;
    $: pendingCount = items.filter((i) => i.status === 'draft' || i.status === 'processing').length;

    onMount(() => {
        void loadAll().then(() => {
            if (hasPendingItems(items)) startPolling();
        });
    });

    onDestroy(() => {
        stopPolling();
    });

    function hasPendingItems(list: KnowledgeSourceItem[]): boolean {
        return list.some((i) => i.status === 'draft' || i.status === 'processing');
    }

    function startPolling(): void {
        if (pollTimer) return;
        isPolling = true;
        pollStartedAt = Date.now();
        pollTimer = setInterval(() => {
            void pollOnce();
        }, POLL_INTERVAL_MS);
    }

    function stopPolling(): void {
        if (pollTimer) {
            clearInterval(pollTimer);
            pollTimer = null;
        }
        isPolling = false;
    }

    async function pollOnce(): Promise<void> {
        if (!teamUuid) return;
        if (Date.now() - pollStartedAt > MAX_POLL_MS) {
            stopPolling();
            return;
        }
        await refreshItems();
        if (!hasPendingItems(items)) {
            stopPolling();
        }
    }

    async function refreshItems(): Promise<void> {
        if (!teamUuid) return;
        try {
            const knowledgeRes: any = await api.getTeamKnowledge();
            if (knowledgeRes?.success) {
                items = knowledgeRes.data?.items ?? [];
            }
        } catch (error) {
            console.error(error);
        }
    }

    $: if (teamUuid) {
        filtered = filterItems(items, search);
    }

    function filterItems(list: KnowledgeSourceItem[], phrase: string): KnowledgeSourceItem[] {
        const q = phrase.trim().toLowerCase();
        if (!q) return list;
        return list.filter(
            (item) =>
                item.title.toLowerCase().includes(q) ||
                (item.file_name?.toLowerCase().includes(q) ?? false),
        );
    }

    async function loadAll(): Promise<void> {
        if (!teamUuid) {
            isLoading = false;
            return;
        }

        isLoading = true;
        try {
            const [knowledgeRes, membersRes]: any[] = await Promise.all([
                api.getTeamKnowledge(),
                api.getTeamMembers(teamUuid),
            ]);

            if (knowledgeRes?.success) {
                items = knowledgeRes.data?.items ?? [];
            } else {
                items = [];
                if (knowledgeRes?.message) {
                    toast.showNotification(false, knowledgeRes.message);
                }
            }

            if (membersRes?.success && userUuid) {
                const me = (membersRes.data?.members ?? []).find((m: any) => m.user_uuid === userUuid);
                canManage = me?.role === 'owner' && me?.status === 'active';
            }
        } catch (error) {
            console.error(error);
            items = [];
            toast.showNotification(false, 'Could not load Team Knowledge');
        } finally {
            isLoading = false;
            if (hasPendingItems(items)) {
                startPolling();
            }
        }
    }

    function openAdd(): void {
        editing = null;
        modalOpen = true;
    }

    function openEdit(item: KnowledgeSourceItem): void {
        if (!canManage) return;
        editing = item;
        modalOpen = true;
    }

    function closeModal(): void {
        modalOpen = false;
        editing = null;
    }

    async function handleSave(
        event: CustomEvent<{
            title: string;
            body_text: string;
            content_kind: string;
            file: File | null;
            allow_override: boolean;
        }>,
    ): Promise<void> {
        saving = true;
        const payload = event.detail;
        try {
            if (editing) {
                const bodyChanged = payload.body_text !== (editing.body_text ?? '').trim();
                const updateData: Record<string, unknown> = {
                    title: payload.title,
                    content_kind: payload.content_kind,
                };
                if (bodyChanged) {
                    updateData.body_text = payload.body_text;
                    updateData.allow_override = payload.allow_override;
                    updateData.reindex = true;
                }
                const response: any = await api.updateTeamKnowledge(editing.uuid, updateData);
                if (response?.success) {
                    toast.showNotification(true, 'Updated');
                    closeModal();
                    await loadAll();
                    if (bodyChanged) startPolling();
                } else {
                    toast.showNotification(false, response?.message || 'Update failed');
                }
                return;
            }

            const formData = new FormData();
            formData.append('title', payload.title);
            if (payload.body_text) formData.append('body_text', payload.body_text);
            formData.append('content_kind', payload.content_kind);
            if (payload.allow_override) formData.append('allow_override', '1');
            if (payload.file) formData.append('file', payload.file);

            const response: any = await api.createTeamKnowledge(formData);
            if (response?.success) {
                toast.showNotification(true, 'Added to Team Knowledge');
                closeModal();
                await loadAll();
                startPolling();
            } else {
                toast.showNotification(false, response?.message || 'Could not add item');
            }
        } catch (error) {
            console.error(error);
            toast.showNotification(false, 'Save failed');
        } finally {
            saving = false;
        }
    }

    function confirmDelete(item: KnowledgeSourceItem): void {
        deleteTarget = item;
        showDeleteConfirm = true;
    }

    async function deleteItem(): Promise<void> {
        if (!deleteTarget) return;
        const uuid = deleteTarget.uuid;
        deleteTarget = null;
        showDeleteConfirm = false;

        const response: any = await api.deleteTeamKnowledge(uuid);
        toast.showNotification(!!response?.success, response?.message || (response?.success ? 'Deleted' : 'Delete failed'));
        if (response?.success) await loadAll();
    }

    function openFile(item: KnowledgeSourceItem): void {
        if (!item.file_url) return;
        window.open(item.file_url, '_blank', 'noopener,noreferrer');
    }

    function openTile(item: KnowledgeSourceItem): void {
        if (item.file_url) {
            openFile(item);
            return;
        }
        if (canManage) openEdit(item);
    }

    async function retryItem(item: KnowledgeSourceItem): Promise<void> {
        retryingUuid = item.uuid;
        try {
            const response: any = await api.retryTeamKnowledge(item.uuid);
            toast.showNotification(!!response?.success, response?.message || (response?.success ? 'Retrying…' : 'Retry failed'));
            if (response?.success) {
                await loadAll();
                startPolling();
            }
        } catch (error) {
            console.error(error);
            toast.showNotification(false, 'Retry failed');
        } finally {
            retryingUuid = null;
        }
    }
</script>

<div class="team-knowledge-page">
    <AppPageHeader
        title="Team Knowledge"
        subtitle="Documents and notes your AI agents can use for your team."
        showAction={false}
    >
        <svelte:fragment slot="actions">
            {#if canManage}
                <button type="button" class="add-btn" on:click={openAdd}>
                    <i class="plus icon" aria-hidden="true"></i>
                    Add
                </button>
            {/if}
        </svelte:fragment>
    </AppPageHeader>

    {#if !teamUuid}
        <AppEmptyState
            icon="users"
            title="No active team"
            description="Join or create a team to use Team Knowledge."
        />
    {:else if isLoading}
        <AppLoadingState label="Loading Team Knowledge…" />
    {:else if items.length === 0}
        <AppEmptyState
            icon="book"
            title="Nothing here yet"
            description="Add past research, reports, or short notes about your organisation. Keep it simple — one item per document or topic."
        >
            {#if canManage}
                <button type="button" class="add-btn empty" on:click={openAdd}>Add your first item</button>
            {:else}
                <p class="read-only-hint">Only team owners can add or edit Team Knowledge.</p>
            {/if}
        </AppEmptyState>
    {:else}
        {#if !canManage}
            <p class="read-only-hint banner">View only — team owners can add and edit items.</p>
        {/if}

        {#if isPolling && pendingCount > 0}
            <p class="polling-banner" role="status" aria-live="polite">
                <i class="circle notch loading icon" aria-hidden="true"></i>
                Indexing {pendingCount} {pendingCount === 1 ? 'item' : 'items'}…
            </p>
        {/if}

        <AppToolbar
            bind:searchValue={search}
            searchPlaceholder="Search by title…"
            resultsCount={filtered.length}
        />

        <ul class="tile-grid">
            {#each filtered as item (item.uuid)}
                <li class="tile" class:tile--indexing={isKnowledgeIndexing(item.status)}>
                    <div class="tile-header">
                        <span class="tile-icon" class:tile-icon--indexing={isKnowledgeIndexing(item.status)} aria-hidden="true">
                            {#if isKnowledgeIndexing(item.status)}
                                <i class="circle notch loading icon"></i>
                            {:else}
                                <i class="{teamTileIcon(item)} icon"></i>
                            {/if}
                        </span>
                        {#if canManage}
                            <div class="tile-actions">
                                <button
                                    type="button"
                                    class="icon-action"
                                    aria-label="Edit {item.title}"
                                    on:click={() => openEdit(item)}
                                >
                                    <i class="edit icon" aria-hidden="true"></i>
                                </button>
                                <button
                                    type="button"
                                    class="icon-action danger"
                                    aria-label="Delete {item.title}"
                                    on:click={() => confirmDelete(item)}
                                >
                                    <i class="trash icon" aria-hidden="true"></i>
                                </button>
                            </div>
                        {/if}
                    </div>

                    <button type="button" class="tile-body" on:click={() => openTile(item)}>
                        <h3 class="tile-title">{item.title}</h3>
                        <p class="tile-meta">
                            {teamTypeLabel(item)}
                            {#if item.file_name}
                                · {formatTeamFileSize(item.file_size)}
                            {/if}
                        </p>
                        <p class="tile-date">{formatTeamKnowledgeDate(item.updated_at)}</p>
                    </button>

                    <div class="tile-footer">
                        <div class="tile-status">
                            <StatusBadge
                                label={teamKnowledgeStatusLabel(item.status)}
                                tone={teamKnowledgeStatusTone(item.status)}
                                loading={isKnowledgeIndexing(item.status)}
                            />
                            {#if item.status === 'failed' && teamKnowledgeFailedHint(item)}
                                <p class="tile-fail-hint">{teamKnowledgeFailedHint(item)}</p>
                            {/if}
                        </div>
                        {#if canManage && item.status === 'failed'}
                            <button
                                type="button"
                                class="retry-btn"
                                on:click={() => retryItem(item)}
                                disabled={retryingUuid === item.uuid}
                            >
                                {retryingUuid === item.uuid ? 'Retrying…' : 'Retry'}
                            </button>
                        {/if}
                    </div>
                </li>
            {/each}
        </ul>

        {#if filtered.length === 0}
            <p class="no-results">No items match your search.</p>
        {/if}
    {/if}
</div>

<TeamKnowledgeFormModal
    open={modalOpen}
    {saving}
    {editing}
    on:close={closeModal}
    on:save={handleSave}
/>

<ModalConfirm on:yes={deleteItem} bind:showModal={showDeleteConfirm}>
    <div slot="body">
        <p>Delete “{deleteTarget?.title}”? This cannot be undone.</p>
    </div>
</ModalConfirm>

<style>
    .team-knowledge-page {
        width: 100%;
        padding: var(--space-5) var(--space-6);
    }

    .add-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        border: 0;
        border-radius: var(--radius-cta);
        padding: var(--space-3) var(--space-5);
        background: var(--primary);
        color: var(--white);
        font-weight: 500;
        cursor: pointer;
        box-shadow: var(--shadow-cta);
    }

    .add-btn.empty {
        margin-top: var(--space-4);
    }

    .add-btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .read-only-hint {
        margin: 0 0 var(--space-4);
        font-size: var(--text-sm);
        color: var(--grey-secondary);
    }

    .read-only-hint.banner {
        padding: var(--space-3) var(--space-4);
        background: var(--greyish);
        border-radius: var(--radius-button);
    }

    .polling-banner {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin: 0 0 var(--space-4);
        padding: var(--space-3) var(--space-4);
        background: var(--status-info-soft);
        border-radius: var(--radius-button);
        color: var(--status-info-ink);
        font-size: var(--text-sm);
    }

    .tile-grid {
        list-style: none;
        margin: var(--space-4) 0 0;
        padding: 0;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: var(--space-4);
    }

    .tile {
        display: flex;
        flex-direction: column;
        background: var(--white);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        min-height: 11rem;
    }

    .tile-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: var(--space-4) var(--space-4) 0;
    }

    .tile-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.75rem;
        height: 2.75rem;
        border-radius: var(--radius-button);
        background: var(--status-info-soft);
        color: var(--primary);
    }

    .tile--indexing {
        border-color: var(--primary);
    }

    .tile-icon--indexing {
        background: var(--status-info-soft);
        color: var(--status-info-ink);
    }

    .tile-actions {
        display: flex;
        gap: var(--space-1);
    }

    .tile-body {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
        padding: var(--space-3) var(--space-4) var(--space-4);
        border: 0;
        background: transparent;
        text-align: left;
        cursor: pointer;
        font: inherit;
        color: var(--grey);
        min-width: 0;
    }

    .tile-body:hover {
        background: var(--greyish);
    }

    .tile-body:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .tile-title {
        margin: 0;
        font-size: var(--text-base);
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        line-height: 1.4;
    }

    .tile-meta,
    .tile-date {
        margin: 0;
        font-size: var(--text-sm);
        color: var(--grey-secondary);
    }

    .tile-footer {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-4) var(--space-4);
        border-top: 1px solid var(--border-color);
    }

    .tile-status {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
        min-width: 0;
    }

    .tile-fail-hint {
        margin: 0;
        font-size: var(--text-xs);
        color: var(--grey-secondary);
        line-height: 1.4;
    }

    .retry-btn {
        border: 0;
        background: transparent;
        color: var(--primary);
        font-size: var(--text-sm);
        font-weight: 500;
        text-decoration: underline;
        cursor: pointer;
        padding: 0;
    }

    .retry-btn:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    .retry-btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .icon-action {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.25rem;
        height: 2.25rem;
        border: 0;
        border-radius: var(--radius-button);
        background: transparent;
        color: var(--grey-secondary);
        cursor: pointer;
    }

    .icon-action:hover {
        background: var(--greyish);
        color: var(--grey);
    }

    .icon-action.danger:hover {
        color: var(--status-danger-ink);
    }

    .icon-action:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .no-results {
        margin: var(--space-6) 0;
        text-align: center;
        color: var(--grey-secondary);
    }

    @media (max-width: 768px) {
        .team-knowledge-page {
            padding: var(--space-4);
        }

        .tile-grid {
            grid-template-columns: 1fr;
        }
    }
</style>
